import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Input, type InputProps } from "../Input";
import EventIcon from "@mui/icons-material/Event";
import { DatePickerRenderer } from "./shared";
import { IconButton, InputAdornment } from "@mui/material";
import { parseRelativeTime, secondsToRelativeFormat } from "./datemath";
import { DateTime } from "@dmca/utils/luxon";
import type { SharedDatePickerProps } from "./useSharedProps";

export type RelativeDateTimePickerOnChangeValue =
  | {
      /** Absolute DateTime object */
      absolute?: DateTime;
      /** Number of seconds in the past relative to now. Positive number is the past, negative number is the future. */
      relative?: number;
      invalid: false;
      inputMethod: "relative" | "absolute";
    }
  | { invalid: true };

export type RelativeDateTimePickerInputProps = Omit<
  InputProps,
  "value" | "onChange"
> &
  Omit<SharedDatePickerProps, "anchorEl" | "onChange" | "value" | "type"> & {
    onChange?: (date: RelativeDateTimePickerOnChangeValue) => void;
    value?: number | DateTime;
  };

export const RelativeDateTimePickerInput = forwardRef<
  any,
  RelativeDateTimePickerInputProps
>(
  (
    {
      dateFormat = "yyyy-MM-dd hh:mm a",
      value: luxonValue,
      onChange: setLuxonValue,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => innerRef.current);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const formatValue = (
      value: number | DateTime | undefined,
      format: string
    ) =>
      typeof value === "number"
        ? secondsToRelativeFormat(value)
        : value?.isValid
        ? value.toFormat(format)
        : "";
    const valueKey = (value: number | DateTime | undefined, format: string) =>
      `${
        typeof value === "number" ? value : value?.toISO() ?? "empty"
      }|${format}`;
    const [inputValue, setInputValue] = useState(() =>
      formatValue(luxonValue, dateFormat)
    );
    const [datePickerValue, setDatePickerValue] = useState<
      DateTime | undefined
    >(() => parseRelativeTime(formatValue(luxonValue, dateFormat), dateFormat));
    const focused = useRef(false);
    const dirty = useRef(false);
    const emittedKey = useRef<string | undefined>(undefined);
    const observedKey = useRef(valueKey(luxonValue, dateFormat));
    const deferredExternal = useRef<
      { value: number | DateTime | undefined; format: string } | undefined
    >(undefined);
    const onChangeRef = useRef(setLuxonValue);
    onChangeRef.current = setLuxonValue;

    const emit = (change: RelativeDateTimePickerOnChangeValue) => {
      if (!change.invalid)
        emittedKey.current = valueKey(change[change.inputMethod], dateFormat);
      onChangeRef.current?.(change);
    };
    const syncExternal = (
      value: number | DateTime | undefined,
      format: string
    ) => {
      const text = formatValue(value, format);
      setInputValue(text);
      setDatePickerValue(parseRelativeTime(text, format));
      dirty.current = false;
      if (
        value === undefined ||
        (typeof value === "number" ? !Number.isFinite(value) : !value.isValid)
      ) {
        onChangeRef.current?.({ invalid: true });
      } else {
        onChangeRef.current?.(
          typeof value === "number"
            ? { invalid: false, inputMethod: "relative", relative: value }
            : { invalid: false, inputMethod: "absolute", absolute: value }
        );
      }
    };

    useEffect(() => {
      const key = valueKey(luxonValue, dateFormat);
      if (key === observedKey.current) return;
      observedKey.current = key;
      if (key === emittedKey.current) {
        emittedKey.current = undefined;
        return; // Parent echo: keep the user's expression/caret while typing.
      }
      if (focused.current && dirty.current) {
        deferredExternal.current = { value: luxonValue, format: dateFormat };
        onChangeRef.current?.({ invalid: true });
      } else {
        deferredExternal.current = undefined;
        syncExternal(luxonValue, dateFormat);
      }
    }, [luxonValue, dateFormat]);

    const handleCalendarButtonClick = () => {
      const parsed = parseRelativeTime(inputValue, dateFormat);
      setDatePickerValue(parsed?.set({ second: 0, millisecond: 0 }));
      setAnchorEl((old) => (old ? null : innerRef.current));
    };
    const handleClose = () => setAnchorEl(null);

    const onInputValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      dirty.current = true;
      setInputValue(text);
      const reference = DateTime.now();
      const time = parseRelativeTime(text, dateFormat, reference);
      setDatePickerValue(time);
      if (!time || deferredExternal.current) {
        emit({ invalid: true });
        return;
      }
      emit({
        absolute: time,
        relative: reference.diff(time, "seconds").as("seconds"),
        invalid: false,
        inputMethod: text.trim().startsWith("now") ? "relative" : "absolute",
      });
    };
    const inputIsValid =
      parseRelativeTime(inputValue, dateFormat) !== undefined;

    const onUIDateChange = (date: DateTime | null) => {
      const selected = date?.set({ second: 0, millisecond: 0 });
      deferredExternal.current = undefined;
      dirty.current = false;
      setInputValue(selected?.isValid ? selected.toFormat(dateFormat) : "");
      setDatePickerValue(selected?.isValid ? selected : undefined);
      if (!selected?.isValid) emit({ invalid: true });
      else
        emit({ absolute: selected, invalid: false, inputMethod: "absolute" });
    };

    return (
      <>
        <Input
          ref={innerRef}
          {...props}
          value={inputValue}
          onChange={onInputValueChange}
          onFocus={(event) => {
            focused.current = true;
            props.onFocus?.(event);
          }}
          onBlur={(event) => {
            focused.current = false;
            const external = deferredExternal.current;
            deferredExternal.current = undefined;
            if (external) syncExternal(external.value, external.format);
            props.onBlur?.(event);
          }}
          error={props.error || !inputIsValid}
          placeholder="now-6h"
          slotProps={{
            input: {
              ...props.InputProps,
              ...(props.slotProps?.input ?? ({} as any)),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={`Open ${props.label ?? "date"} calendar`}
                    onClick={handleCalendarButtonClick}
                  >
                    <EventIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <DatePickerRenderer
          anchorEl={anchorEl}
          onClose={handleClose}
          dateFormat={dateFormat}
          value={datePickerValue}
          onChange={onUIDateChange}
          type="datetime"
        />
      </>
    );
  }
);
